import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, Settings, Sparkles, X } from '../components/icons';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';
import { authService } from '../services/authService';
import { evolutionService } from '../services/evolutionService';
import { useToast } from '../store/toastContext';

const BASE_URL = 'https://apiwhattsdom-evolution-api.znwkmn.easypanel.host/';
const API_KEY = '429683C4C977415CAAFCCE10F7D57E11';
const STORAGE_KEY = 'captacap_whatts_v2';
const AUTH_URL =
  process.env.REACT_APP_N8N_AUTH_URL ||
  'https://apiwhattsdom-n8n.znwkmn.easypanel.host/webhook/78fd29a4-4c21-4abd-b45d-b0a49f27ca85';

type Step = 1 | 2 | 3 | 4 | 5 | 6;
const LOADING_STEPS = [
  'Validando credenciais e instancia...',
  'Sincronizando status do WhatsApp...',
  'Aplicando webhook e eventos...',
  'Atualizando configuracoes de atendimento...'
];

const defaultState = {
  instanceName: '',
  instanceToken: '',
  integration: 'WHATSAPP-BAILEYS' as 'WHATSAPP-BAILEYS' | 'WHATSAPP-BUSINESS' | 'EVOLUTION',
  webhookUrl: '',
  webhookEnabled: true,
  webhookByEvents: true,
  webhookBase64: false,
  messagesUpsert: true,
  groupsIgnore: true,
  alwaysOnline: true,
  readMessages: false,
  rejectCall: false,
  msgCall: 'Nao atendemos chamadas.'
};

const autoToken = (instanceName: string) => {
  const random = Math.random().toString(36).slice(2, 10);
  const base = (instanceName.trim() || 'instancia').toLowerCase().replace(/[^a-z0-9_-]/g, '-');
  return `${base}-${random}`;
};

const isConnectedState = (value: string) => ['open', 'connected', 'online', 'ready'].includes(value.trim().toLowerCase());

const extractString = (source: unknown, path: string[]): string => {
  let current: unknown = source;
  for (const key of path) {
    if (!current || typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === 'string' ? current : '';
};

const extractBool = (source: unknown, path: string[]): boolean | null => {
  let current: unknown = source;
  for (const key of path) {
    if (!current || typeof current !== 'object') return null;
    current = (current as Record<string, unknown>)[key];
  }

  if (typeof current === 'boolean') return current;
  if (typeof current === 'string') {
    const normalized = current.trim().toLowerCase();
    if (['true', '1', 'yes', 'sim', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'nao', 'off'].includes(normalized)) return false;
  }
  if (typeof current === 'number') return current === 1;
  return null;
};

const extractArray = (source: unknown, path: string[]): unknown[] | null => {
  let current: unknown = source;
  for (const key of path) {
    if (!current || typeof current !== 'object') return null;
    current = (current as Record<string, unknown>)[key];
  }
  return Array.isArray(current) ? current : null;
};

const pickInstanceName = (item: unknown) =>
  extractString(item, ['name']) ||
  extractString(item, ['instance']) ||
  extractString(item, ['instance', 'instanceName']) ||
  extractString(item, ['instanceName']) ||
  extractString(item, ['instance', 'name']) ||
  extractString(item, ['instance', 'instance']);

const pickInstanceToken = (item: unknown) =>
  extractString(item, ['token']) ||
  extractString(item, ['instance', 'token']) ||
  extractString(item, ['apikey']) ||
  extractString(item, ['apiKey']) ||
  extractString(item, ['instance', 'apikey']) ||
  extractString(item, ['instance', 'apiKey']) ||
  extractString(item, ['hash', 'apikey']) ||
  extractString(item, ['hash', 'apiKey']);

const unwrapFirst = <T,>(payload: T | T[] | null): T | null => {
  if (!payload) return null;
  if (Array.isArray(payload)) return payload[0] || null;
  return payload;
};

export default function ConfigurarWhattsPage() {
  const { pushToast } = useToast();
  const [form, setForm] = useState(defaultState);
  const [step, setStep] = useState<Step>(1);
  const [tokenEditable, setTokenEditable] = useState(false);
  const [connected, setConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('');
  const [qrImage, setQrImage] = useState('');
  const [qrText, setQrText] = useState('');
  const [polling, setPolling] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const lastSyncedRef = useRef<{ instance: string; apikey: string } | null>(null);

  const credentials = useMemo(() => ({ baseUrl: BASE_URL, apiKey: API_KEY }), []);

  const events = useMemo(() => (form.messagesUpsert ? ['MESSAGES_UPSERT'] : []), [form.messagesUpsert]);
  const showLoadingOverlay = busy === 'create' || busy === 'qr';
  const loadingTitle = useMemo(() => {
    if (busy === 'create') return 'Criando instancia Evolution';
    if (busy === 'qr') return 'Gerando QR Code';
    return 'Sincronizando instancia';
  }, [busy]);

  const lockStep = (value: Step) => setStep((current) => (current > value ? current : value));

  const run = async (id: string, fn: () => Promise<void>) => {
    setBusy(id);
    try {
      await fn();
    } catch (error) {
      pushToast({ type: 'error', title: error instanceof Error ? error.message : 'Falha na operacao.' });
    } finally {
      setBusy(null);
    }
  };

  const getCurrentUserToken = () => {
    const current = authService.getSession();
    const token = current?.user?.id?.trim() || current?.token?.trim() || '';
    if (!token) throw new Error('Sessao invalida. Faca login novamente.');
    return token;
  };

  const syncInstanceAndApiKeyToUser = async (instanceName: string, instanceApiKey: string) => {
    const userToken = getCurrentUserToken();

    let apiKeySerp = '';
    let apiKeyApolo = '';
    try {
      const getUsersResponse = await fetch(AUTH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          route: 'get_users',
          token: userToken
        })
      });
      if (getUsersResponse.ok) {
        const raw = (await getUsersResponse.json().catch(() => null)) as Record<string, unknown> | Array<Record<string, unknown>> | null;
        const user = unwrapFirst(raw);
        if (user) {
          apiKeySerp = extractString(user, ['api_key_serp']);
          apiKeyApolo = extractString(user, ['api_key_apolo']);
        }
      }
    } catch {
      // segue com vazio
    }

    const updateResponse = await fetch(AUTH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        route: 'update_configuracao',
        token: userToken,
        api_key_serp: apiKeySerp,
        api_key_apolo: apiKeyApolo,
        apikey: instanceApiKey,
        instance: instanceName
      })
    });

    if (!updateResponse.ok) {
      const text = await updateResponse.text().catch(() => '');
      throw new Error(`Falha ao atualizar instance/apikey na base (${updateResponse.status}) ${text}`.trim());
    }
  };

  const refreshConnection = async (silent = false) => {
    if (!form.instanceName.trim()) return;
    const response = await evolutionService.getConnectionState(credentials, form.instanceName.trim());
    const state =
      extractString(response, ['instance', 'state']) || extractString(response, ['state']) || extractString(response, ['instance', 'status']) || 'unknown';
    setConnectionState(state);
    if (isConnectedState(state)) {
      setConnected(true);
      setPolling(false);
      lockStep(4);
      if (!silent) pushToast({ type: 'success', title: 'Instancia conectada.' });
    } else {
      setConnected(false);
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as typeof defaultState;
        const next = {
          ...defaultState,
          ...parsed
        };
        if (!next.instanceToken && next.instanceName) next.instanceToken = autoToken(next.instanceName);
        setForm(next);
      }
    } catch {
      // ignore
    } finally {
      setHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
  }, [form, hydrated]);

  useEffect(() => {
    if (!polling || !form.instanceName.trim()) return;
    const timer = window.setInterval(() => {
      refreshConnection(true).catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [polling, form.instanceName]);

  useEffect(() => {
    if (!showLoadingOverlay) {
      setLoadingStep(0);
      return;
    }
    const timer = window.setInterval(() => {
      setLoadingStep((prev) => (prev + 1) % LOADING_STEPS.length);
    }, 1300);
    return () => window.clearInterval(timer);
  }, [showLoadingOverlay]);

  useEffect(() => {
    if (!hydrated) return;
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const currentSession = authService.getSession();
        const userToken = getCurrentUserToken();
        let instanceFromGetUsers = '';
        let apikeyFromGetUsers = '';
        let webhookFromGetUsers = '';
        try {
          const userResponse = await fetch(AUTH_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              route: 'get_users',
              token: userToken
            })
          });

          if (userResponse.ok) {
            const userRaw = (await userResponse.json().catch(() => null)) as Record<string, unknown> | Array<Record<string, unknown>> | null;
            const user = unwrapFirst(userRaw);
            if (user && !cancelled) {
              const userInstance = extractString(user, ['instance']);
              const userApiKey = extractString(user, ['apikey']);
              const userWebhook = extractString(user, ['url_whatts_api']);
              instanceFromGetUsers = userInstance;
              apikeyFromGetUsers = userApiKey;
              webhookFromGetUsers = userWebhook;

              setForm((prev) => ({
                ...prev,
                instanceName: userInstance || prev.instanceName,
                instanceToken: userApiKey || prev.instanceToken,
                webhookUrl: userWebhook || prev.webhookUrl
              }));
            }
          }
        } catch {
          // segue bootstrap normalmente
        }

        const targetName = (instanceFromGetUsers || currentSession?.user?.instance || form.instanceName || '').trim();
        const targetApiKey = (apikeyFromGetUsers || form.instanceToken || '').trim();
        let foundName = '';
        let foundToken = '';
        try {
          const instances = await evolutionService.fetchInstances(credentials);
          if (cancelled) return;
          const list = Array.isArray(instances) ? instances : [];
          const found =
            list.find((item) => {
              const name = pickInstanceName(item).toLowerCase();
              const token = pickInstanceToken(item).toLowerCase();
              return (targetName && name === targetName.toLowerCase()) || (targetApiKey && token === targetApiKey.toLowerCase());
            }) ||
            (targetName ? null : list[0] || null);

          foundName = found ? pickInstanceName(found) : '';
          foundToken = found ? pickInstanceToken(found) : '';
        } catch {
          // Se fetchInstances falhar, seguimos com dados do get_users/local.
        }

        const name = (instanceFromGetUsers || targetName || foundName || '').trim();
        const token = (apikeyFromGetUsers || targetApiKey || foundToken || '').trim();
        if (!name) return;

        setForm((prev) => ({
          ...prev,
          instanceName: name,
          instanceToken: token || prev.instanceToken,
          webhookUrl: webhookFromGetUsers || prev.webhookUrl
        }));
        lockStep(2);

        const response = await evolutionService.getConnectionState(credentials, name);
        if (cancelled) return;
        const state =
          extractString(response, ['instance', 'state']) || extractString(response, ['state']) || extractString(response, ['instance', 'status']) || 'unknown';
        setConnectionState(state);
        if (isConnectedState(state)) {
          setConnected(true);
          setPolling(false);
          lockStep(4);
        } else {
          setConnected(false);
          lockStep(3);
          setPolling(true);
        }

        try {
          const webhook = await evolutionService.findWebhook(credentials, name);
          if (cancelled) return;
          const enabled =
            extractBool(webhook, ['instance', 'enabled']) ??
            extractBool(webhook, ['webhook', 'enabled']) ??
            extractBool(webhook, ['enabled']) ??
            false;
          const webhookByEvents =
            extractBool(webhook, ['instance', 'webhookByEvents']) ??
            extractBool(webhook, ['webhook', 'webhookByEvents']) ??
            extractBool(webhook, ['webhookByEvents']) ??
            true;
          const webhookBase64 =
            extractBool(webhook, ['instance', 'webhookBase64']) ??
            extractBool(webhook, ['webhook', 'webhookBase64']) ??
            extractBool(webhook, ['webhookBase64']) ??
            false;
          const url =
            extractString(webhook, ['instance', 'url']) ||
            extractString(webhook, ['webhook', 'url']) ||
            extractString(webhook, ['url']);
          const apiEventsRaw =
            extractArray(webhook, ['instance', 'events']) ||
            extractArray(webhook, ['webhook', 'events']) ||
            extractArray(webhook, ['events']) ||
            [];
          const apiEvents = apiEventsRaw.map((item) => String(item));
          setForm((prev) => ({
            ...prev,
            webhookEnabled: enabled,
            webhookByEvents,
            webhookBase64,
            webhookUrl: url || prev.webhookUrl,
            messagesUpsert: apiEvents.map((e) => e.toUpperCase()).includes('MESSAGES_UPSERT')
          }));
        } catch {
          // ignore
        }

        try {
          const settings = await evolutionService.findSettings(credentials, name);
          if (cancelled) return;
          const groupsIgnore =
            extractBool(settings, ['instance', 'groupsIgnore']) ??
            extractBool(settings, ['settings', 'groupsIgnore']) ??
            extractBool(settings, ['groupsIgnore']) ??
            false;
          const alwaysOnline =
            extractBool(settings, ['instance', 'alwaysOnline']) ??
            extractBool(settings, ['settings', 'alwaysOnline']) ??
            extractBool(settings, ['alwaysOnline']) ??
            false;
          const readMessages =
            extractBool(settings, ['instance', 'readMessages']) ??
            extractBool(settings, ['settings', 'readMessages']) ??
            extractBool(settings, ['readMessages']) ??
            false;
          const rejectCall =
            extractBool(settings, ['instance', 'rejectCall']) ??
            extractBool(settings, ['settings', 'rejectCall']) ??
            extractBool(settings, ['rejectCall']) ??
            false;
          const msgCall =
            extractString(settings, ['instance', 'msgCall']) || extractString(settings, ['settings', 'msgCall']) || extractString(settings, ['msgCall']);

          setForm((prev) => ({
            ...prev,
            groupsIgnore,
            alwaysOnline,
            readMessages,
            rejectCall,
            msgCall: msgCall || prev.msgCall
          }));
        } catch {
          // ignore
        }
      } catch {
        // ignore
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [credentials, hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    const instance = form.instanceName.trim();
    const apikey = form.instanceToken.trim();
    if (!instance || !apikey) return;
    if (instance.toLowerCase() === apikey.toLowerCase()) return;

    if (lastSyncedRef.current && lastSyncedRef.current.instance === instance && lastSyncedRef.current.apikey === apikey) {
      return;
    }

    const timer = window.setTimeout(() => {
      syncInstanceAndApiKeyToUser(instance, apikey)
        .then(() => {
          lastSyncedRef.current = { instance, apikey };
        })
        .catch(() => undefined);
    }, 800);

    return () => window.clearTimeout(timer);
  }, [form.instanceName, form.instanceToken, hydrated]);

  const createInstance = async () =>
    run('create', async () => {
      const instanceName = form.instanceName.trim();
      if (!instanceName) throw new Error('Informe o nome da instancia.');
      const token = form.instanceToken.trim() || autoToken(instanceName);
      if (token.toLowerCase() === instanceName.toLowerCase()) throw new Error('Token deve ser diferente do nome da instancia.');

      await evolutionService.createInstance(credentials, {
        instanceName,
        token,
        qrcode: true,
        integration: form.integration
      });
      await syncInstanceAndApiKeyToUser(instanceName, token);
      lastSyncedRef.current = { instance: instanceName, apikey: token };

      setForm((prev) => ({ ...prev, instanceToken: token }));
      setTokenEditable(false);
      setConnected(false);
      setPolling(false);
      setQrImage('');
      setQrText('');
      setConnectionState('');
      lockStep(2);
      pushToast({ type: 'success', title: 'Instancia criada.' });
    });

  const connectQr = async () =>
    run('qr', async () => {
      const instanceName = form.instanceName.trim();
      if (!instanceName) throw new Error('Crie a instancia primeiro.');
      const response = await evolutionService.connectInstance(credentials, instanceName);
      const base64 =
        extractString(response, ['qrcode', 'base64']) || extractString(response, ['base64']) || extractString(response, ['qrcode']);
      const pairCode = extractString(response, ['pairingCode']) || extractString(response, ['code']);

      if (base64) {
        setQrImage(base64.startsWith('data:image') ? base64 : `data:image/png;base64,${base64}`);
        setQrText('');
      } else {
        setQrImage('');
        setQrText(pairCode || 'QR indisponivel, tente novamente.');
      }

      setPolling(true);
      lockStep(3);
      pushToast({ type: 'info', title: 'QR gerado. Leia com o WhatsApp.' });
    });

  const saveWebhook = async () =>
    run('webhook', async () => {
      const instanceName = form.instanceName.trim();
      if (!instanceName) throw new Error('Instancia nao informada.');
      if (form.webhookEnabled && !form.webhookUrl.trim()) throw new Error('Informe a URL de retorno do n8n.');
      await evolutionService.setWebhook(credentials, instanceName, {
        enabled: form.webhookEnabled,
        url: form.webhookUrl.trim(),
        webhookByEvents: form.webhookByEvents,
        webhookBase64: form.webhookBase64,
        events
      });
      lockStep(5);
      pushToast({ type: 'success', title: 'Webhook salvo.' });
    });

  const saveSettings = async () =>
    run('settings', async () => {
      const instanceName = form.instanceName.trim();
      if (!instanceName) throw new Error('Instancia nao informada.');
      await evolutionService.setSettings(credentials, instanceName, {
        rejectCall: form.rejectCall,
        msgCall: form.msgCall.trim(),
        groupsIgnore: form.groupsIgnore,
        alwaysOnline: form.alwaysOnline,
        readMessages: form.readMessages,
        readStatus: false,
        syncFullHistory: false
      });
      lockStep(6);
      pushToast({ type: 'success', title: 'Configuracoes WhatsApp salvas.' });
    });

  const saveAll = async () =>
    run('save-all', async () => {
      await saveWebhook();
      await saveSettings();
    });

  const deleteInstance = async () =>
    run('delete', async () => {
      const instanceName = form.instanceName.trim();
      if (!instanceName) throw new Error('Instancia nao informada.');
      await evolutionService.deleteInstance(credentials, instanceName);
      setConnected(false);
      setPolling(false);
      setConnectionState('');
      setQrImage('');
      setQrText('');
      setForm(defaultState);
      setStep(1);
      localStorage.removeItem(STORAGE_KEY);
      pushToast({ type: 'success', title: 'Instancia deletada.' });
    });

  return (
    <div className="relative space-y-4">
      {showLoadingOverlay ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/65 px-4 backdrop-blur-md">
          <Card className="google-search-loading-card relative w-full max-w-2xl space-y-5 overflow-hidden">
            <div className="google-search-grid" />
            <div className="google-search-scanline" />
            <div className="google-search-progress" />
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="google-search-scene">
                <div className="google-search-beam" />
                <div className="google-search-ring google-search-ring-a" />
                <div className="google-search-ring google-search-ring-b" />
                <div className="google-search-orbit google-search-orbit-inner" />
                <div className="google-search-orbit google-search-orbit-outer" />
                <div className="google-search-core">
                  <Sparkles className="h-5 w-5" />
                </div>
              </div>
              <div>
                <p className="google-search-title text-sm font-bold text-slate-800 dark:text-slate-100">{loadingTitle}</p>
                <p className="text-xs text-slate-600 dark:text-slate-300">{LOADING_STEPS[loadingStep]}</p>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {LOADING_STEPS.map((stepLabel, index) => (
                <div
                  key={stepLabel}
                  className={`google-search-step rounded-xl border px-3 py-2 text-xs transition-all duration-300 ${
                    index === loadingStep
                      ? 'google-search-step-active border-primary/50 bg-primary/10 text-slate-900 dark:border-cta/50 dark:bg-cta/15 dark:text-slate-100'
                      : 'border-borderLight/70 bg-white/40 text-slate-500 dark:border-borderDark/70 dark:bg-slate-900/20 dark:text-slate-400'
                  }`}
                >
                  {stepLabel}
                </div>
              ))}
            </div>
          </Card>
        </div>
      ) : null}
      <h1 className="text-2xl font-black">Configurar Whatts</h1>

      <Card className="space-y-3">
        <p className="text-sm">
          <strong>Etapa:</strong>{' '}
          {step === 1 && '1/5 Criar instancia'}
          {step === 2 && '2/5 Gerar QR'}
          {step === 3 && '3/5 Aguardando conexao'}
          {step === 4 && '4/5 Configurar webhook'}
          {step === 5 && '5/5 Configurar opcoes WhatsApp'}
          {step === 6 && 'Concluido'}
        </p>
        {!connected && step >= 3 ? (
          <p className="inline-flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-500" />
            Aguardando leitura do QR...
          </p>
        ) : null}
        {connected ? <p className="text-green-600 dark:text-green-400">Conectado</p> : null}
      </Card>

      <Card className="space-y-3">
        <Input label="Nome da instancia (instance)" value={form.instanceName} onChange={(e) => setForm((p) => ({ ...p, instanceName: e.target.value }))} />
        <div className="space-y-2">
          <Input label="Token da instancia (apikey)" value={form.instanceToken} readOnly={!tokenEditable} onChange={(e) => setForm((p) => ({ ...p, instanceToken: e.target.value }))} />
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setTokenEditable((v) => !v)}>
              {tokenEditable ? 'Bloquear token' : 'Editar token'}
            </Button>
            <Button variant="ghost" onClick={() => setForm((p) => ({ ...p, instanceToken: autoToken(p.instanceName) }))}>
              Gerar token
            </Button>
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300">Canal</label>
          <select
            value={form.integration}
            onChange={(e) => setForm((p) => ({ ...p, integration: e.target.value as typeof p.integration }))}
            className="h-12 w-full rounded-xl border border-borderLight bg-cardLight px-4 text-sm text-textLight outline-none dark:border-borderDark dark:bg-cardDark dark:text-textDark"
          >
            <option value="WHATSAPP-BAILEYS">Baileys</option>
            <option value="WHATSAPP-BUSINESS">WhatsApp Business</option>
            <option value="EVOLUTION">Evolution</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={createInstance} disabled={busy !== null} iconLeft={<Settings />}>
            {busy === 'create' ? 'Criando...' : '1) Criar instancia'}
          </Button>
          <Button variant="outline" onClick={connectQr} disabled={busy !== null || step < 2} iconLeft={<CheckCircle2 />}>
            {busy === 'qr' ? 'Gerando...' : '2) Obter QR Code'}
          </Button>
          <Button variant="outline" onClick={() => run('state', () => refreshConnection())} disabled={busy !== null || step < 3} iconLeft={<ArrowRight />}>
            {busy === 'state' ? 'Consultando...' : '3) Atualizar conexao'}
          </Button>
          <Button variant="ghost" onClick={deleteInstance} disabled={busy !== null || !form.instanceName.trim()} iconLeft={<X />}>
            {busy === 'delete' ? 'Deletando...' : 'Deletar instancia'}
          </Button>
        </div>
        {connectionState ? <p className="text-sm">Status atual: {connectionState}</p> : null}
        {qrImage ? <img src={qrImage} alt="QR Code da instancia" className="h-56 w-56 rounded-lg border border-borderLight bg-white p-2 dark:border-borderDark" /> : null}
        {qrText ? <p className="text-sm font-semibold">Pairing code: {qrText}</p> : null}
      </Card>

      <Card className={`space-y-3 ${step < 4 ? 'opacity-60' : ''}`}>
        <h2 className="text-lg font-black">Webhook (n8n)</h2>
        <Input label="URL retorno n8n" value={form.webhookUrl} onChange={(e) => setForm((p) => ({ ...p, webhookUrl: e.target.value }))} />
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.webhookEnabled} onChange={(e) => setForm((p) => ({ ...p, webhookEnabled: e.target.checked }))} />
          Enabled (ativo)
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.webhookByEvents} onChange={(e) => setForm((p) => ({ ...p, webhookByEvents: e.target.checked }))} />
          Webhook by events
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.webhookBase64} onChange={(e) => setForm((p) => ({ ...p, webhookBase64: e.target.checked }))} />
          Webhook Base64
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.messagesUpsert} onChange={(e) => setForm((p) => ({ ...p, messagesUpsert: e.target.checked }))} />
          messages.upsert
        </label>
        <Button variant="outline" onClick={saveWebhook} disabled={busy !== null || step < 4} iconLeft={<ArrowRight />}>
          {busy === 'webhook' ? 'Salvando...' : '4) Salvar webhook'}
        </Button>
      </Card>

      <Card className={`space-y-3 ${step < 4 ? 'opacity-60' : ''}`}>
        <h2 className="text-lg font-black">Opcoes WhatsApp</h2>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.groupsIgnore} onChange={(e) => setForm((p) => ({ ...p, groupsIgnore: e.target.checked }))} />
          Ignore groups
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.alwaysOnline} onChange={(e) => setForm((p) => ({ ...p, alwaysOnline: e.target.checked }))} />
          Always online
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.readMessages} onChange={(e) => setForm((p) => ({ ...p, readMessages: e.target.checked }))} />
          Read messages
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.rejectCall} onChange={(e) => setForm((p) => ({ ...p, rejectCall: e.target.checked }))} />
          Reject calls
        </label>
        <Input label="Mensagem reject call" value={form.msgCall} onChange={(e) => setForm((p) => ({ ...p, msgCall: e.target.value }))} />
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={saveSettings} disabled={busy !== null || step < 4} iconLeft={<Settings />}>
            {busy === 'settings' ? 'Salvando...' : '5) Salvar opcoes'}
          </Button>
          <Button variant="primary" onClick={saveAll} disabled={busy !== null || step < 4} iconLeft={<CheckCircle2 />}>
            {busy === 'save-all' ? 'Salvando tudo...' : 'Salvar tudo'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
